import commonUtil from "../../util/common-utils";
import { Actions } from "../../constants/actions";
import { UserDisplayHelper, type UserDisplayItem } from "../../user/user-display-utils";
import { WysiwygForm } from "../wysiwyg-form";

interface MentionBoundsLike {
    left: number;
    top: number;
    height: number;
}

export class WysiwygMentionRender {
    public static createDropdown(editorDiv: HTMLElement): HTMLDivElement {
        const dropdownEl = document.createElement("div");
        dropdownEl.className = "post-mention-suggest";
        dropdownEl.id = `post-mention-suggest-${Math.random().toString(36).slice(2, 10)}`;
        dropdownEl.setAttribute("role", "listbox");
        dropdownEl.hidden = true;

        const host = editorDiv.parentElement ?? editorDiv;
        if (host instanceof HTMLElement) {
            host.classList.add("post-mention-suggest-host");
        }
        host.appendChild(dropdownEl);

        return dropdownEl;
    }

    public static getDropdownId(dropdownEl: HTMLElement): string {
        return String(dropdownEl.id || "").trim();
    }

    public static renderLoading(dropdownEl: HTMLElement): void {
        dropdownEl.innerHTML =
            '<div class="search-user-modal__loading"><div class="spinner spinner--inline-load" role="status" aria-hidden="true"></div><div class="search-user-modal__empty">Loading...</div></div>';
        dropdownEl.hidden = false;
        dropdownEl.classList.add("post-mention-suggest--open");
    }

    public static renderEmpty(dropdownEl: HTMLElement): void {
        dropdownEl.innerHTML = '<div class="search-user-modal__empty">-</div>';
        dropdownEl.hidden = false;
        dropdownEl.classList.add("post-mention-suggest--open");
    }

    public static renderItems(dropdownEl: HTMLElement, items: UserDisplayItem[], activeIndex: number): void {
        if (!Array.isArray(items) || items.length === 0) {
            this.renderEmpty(dropdownEl);
            return;
        }

        const dropdownId = this.getDropdownId(dropdownEl);
        dropdownEl.innerHTML = items
            .map((user, index) => {
                const followingClass = user.isFollowing
                    ? " post-mention-suggest__option--following"
                    : " post-mention-suggest__option--muted";
                const isActive = index === activeIndex;
                const optionId = `${dropdownId}-option-${index}`;
                const activeClass = isActive ? " post-mention-suggest__option--active" : "";
                return `<button type="button" id="${commonUtil.escapeHtmlAttribute(optionId)}" role="option" aria-selected="${isActive ? "true" : "false"}" class="post-mention-suggest__option${followingClass}${activeClass}" data-action="${Actions.SELECT_USER}" data-user-id="${commonUtil.escapeHtmlAttribute(
                    user.id,
                )}">
                    <span class="post-mention-suggest__name">${commonUtil.escapeHtml(user.displayName)}</span>
                    <span class="post-mention-suggest__meta">${UserDisplayHelper.buildUserMetaHtml(user)}</span>
                </button>`;
            })
            .join("");
        dropdownEl.hidden = false;
        dropdownEl.classList.add("post-mention-suggest--open");
        this.scrollActiveOptionIntoView(dropdownEl, activeIndex);
    }

    public static getActiveOptionId(dropdownEl: HTMLElement, activeIndex: number): string {
        const dropdownId = this.getDropdownId(dropdownEl);
        if (dropdownId === "" || activeIndex < 0) {
            return "";
        }
        return `${dropdownId}-option-${activeIndex}`;
    }

    public static scrollActiveOptionIntoView(dropdownEl: HTMLElement, activeIndex: number): void {
        const optionId = this.getActiveOptionId(dropdownEl, activeIndex);
        if (optionId === "") {
            return;
        }

        const option = WysiwygForm.getMentionUserOptionById(dropdownEl, optionId);
        option?.scrollIntoView({ block: "nearest" });
    }

    public static positionDropdown(dropdownEl: HTMLElement, bounds: MentionBoundsLike): void {
        const top = Number(bounds.top ?? 0) + Number(bounds.height ?? 0) + 8;
        const left = Number(bounds.left ?? 0);
        dropdownEl.classList.remove("post-mention-suggest--fallback");
        dropdownEl.style.top = `${top}px`;
        dropdownEl.style.left = `${left}px`;
    }

    public static positionDropdownFallback(dropdownEl: HTMLElement): void {
        dropdownEl.style.top = "100%";
        dropdownEl.style.left = "0";
        dropdownEl.classList.add("post-mention-suggest--fallback");
    }

    public static positionDropdownAtCaret(
        dropdownEl: HTMLElement,
        textarea: HTMLTextAreaElement,
        caretOffset: number,
    ): void {
        try {
            const host = dropdownEl.parentElement;
            if (!(host instanceof HTMLElement)) {
                this.positionDropdownFallback(dropdownEl);
                return;
            }
            const hostRect = host.getBoundingClientRect();
            const textareaRect = textarea.getBoundingClientRect();
            const coords = this.measureTextareaCaretCoords(textarea, caretOffset);

            const top =
                textareaRect.top - hostRect.top +
                coords.top - textarea.scrollTop +
                coords.height + 4;
            const left = textareaRect.left - hostRect.left + coords.left - textarea.scrollLeft;

            dropdownEl.classList.remove("post-mention-suggest--fallback");
            dropdownEl.style.top = `${Math.max(0, top)}px`;
            dropdownEl.style.left = `${Math.max(0, left)}px`;
        } catch (_e) {
            this.positionDropdownFallback(dropdownEl);
        }
    }

    private static measureTextareaCaretCoords(
        textarea: HTMLTextAreaElement,
        position: number,
    ): { top: number; left: number; height: number } {
        const style = window.getComputedStyle(textarea);
        const mirror = document.createElement("div");

        mirror.style.position = "absolute";
        mirror.style.top = "-9999px";
        mirror.style.left = "-9999px";
        mirror.style.visibility = "hidden";
        mirror.style.whiteSpace = "pre-wrap";
        mirror.style.wordWrap = "break-word";
        mirror.style.overflow = "hidden";

        for (const prop of [
            "font-family", "font-size", "font-style", "font-variant", "font-weight",
            "letter-spacing", "line-height", "text-transform", "word-spacing",
            "padding-top", "padding-right", "padding-bottom", "padding-left",
            "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
        ]) {
            mirror.style.setProperty(prop, style.getPropertyValue(prop));
        }
        mirror.style.width = `${textarea.offsetWidth}px`;

        mirror.appendChild(document.createTextNode(textarea.value.substring(0, position)));
        const caretSpan = document.createElement("span");
        caretSpan.textContent = "​";
        mirror.appendChild(caretSpan);

        document.body.appendChild(mirror);
        const result = {
            top: caretSpan.offsetTop,
            left: caretSpan.offsetLeft,
            height: caretSpan.offsetHeight,
        };
        document.body.removeChild(mirror);

        return result;
    }

    public static hide(dropdownEl: HTMLElement): void {
        dropdownEl.hidden = true;
        dropdownEl.classList.remove("post-mention-suggest--open");
        dropdownEl.classList.remove("post-mention-suggest--fallback");
        dropdownEl.innerHTML = "";
    }
}
